import React, { type HTMLAttributes, useContext, type FC } from 'react';

import { sendBeaconEvent, isDefined } from '@lucid/utils';
import { useSmartlingStrings } from '@lucid/hooks';
import { getColor } from '@lucid/theme';

import reactHTMLParser from '../../utils/reactHTMLParser';
import Accordion from '../accordion/accordion';
import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import Link from '../link/link';
import type { Lucid } from '../../types/types';
import LayoutContext from '../context/layout-context';

export interface FAQSectionProps extends HTMLAttributes<HTMLDivElement> {
  /** Determines whether light text is needed for darker backgrounds */
  hasDarkBackground?: boolean;
  /** The title for the FAQ section */
  sectionTitle?: string;
  /** Heading variant for the section title */
  headingVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** Array of collapsible FAQ items */
  items: Lucid.Directus.Generated.Faq[];
  /** Adds extra padding to Container in FAQ section*/
  extraPadding?: number;
  /** Background variant for nested Accordion components */
  accordionVariant?: Lucid.Theme.Component.Accordion.Item.VariantKeys;
  /** Variant for navigational link displayed under FAQ item */
  linkVariant?: Lucid.Theme.Component.Link.VariantKeys;
  /** Updates the background color for the FAQ section */
  backgroundColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** Allows componeent to receive additional styles */
  className?: string;
}

const FAQSection: FC<FAQSectionProps> = ({
  backgroundColor = 'coolGrey.100',
  hasDarkBackground = false,
  sectionTitle,
  items,
  extraPadding,
  headingVariant = 'headingXL',
  accordionVariant = 'transparent',
  linkVariant,
  className,
  ...rest
}) => {
  const { shortCode, product } = useContext(LayoutContext);
  const backgroundColorValue = (typeof backgroundColor === 'object' && backgroundColor[product]) || backgroundColor;
  const hasDarkBackgroundColor = hasDarkBackground || Number(backgroundColorValue.split('.')[1]) >= 60;
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_ct_consideration_faq_link_text'],
    shortCode,
  });

  return (
    <Box
      {...rest}
      as="section"
      css={theme => ({
        label: 'faqSection',
        background: getColor({
          color: backgroundColor,
          theme,
        }),
        padding: `${theme.space.s64}px 0`,
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Container extraPadding={extraPadding}>
        <Heading
          as="h2"
          css={theme => ({
            marginBottom: theme.space.s64,
            color: hasDarkBackgroundColor ? 'white' : theme.colors.coolGrey[100],
          })}
          variant={headingVariant}
        >
          {sectionTitle}
        </Heading>
        <Accordion allowMultipleExpanded allowZeroExpanded>
          {items.map((faqItem, idx) => {
            const { question, answer, links } = faqItem;
            const faqLinks = (links?.map(link => link.links_id) || []).filter(isDefined);

            return (
              <Accordion.Item key={faqItem.id} variant={accordionVariant}>
                <Accordion.Heading variant={accordionVariant}>
                  <Accordion.Button
                    hasArrow
                    css={theme => ({ color: hasDarkBackgroundColor ? 'white' : theme.colors.coolGrey[100] })}
                    onClick={() =>
                      sendBeaconEvent({
                        name: 'clickedMarketingPageElement',
                        elementLocation: 'body',
                        elementDetail: 'faq',
                        elementType: 'accordion',
                      })
                    }
                    variant={accordionVariant}
                  >
                    {reactHTMLParser(question)}
                  </Accordion.Button>
                </Accordion.Heading>
                <Accordion.Panel>
                  <Box
                    css={theme => [
                      theme.shared.htmlParserStyles,
                      {
                        color: hasDarkBackgroundColor ? 'white' : theme.colors.coolGrey[100],
                        maxWidth: '100%',
                        flex: '0 1 100%',
                        [theme.breakpoints.bp1024.min]: {
                          flex: '0 1 58%',
                          maxWidth: '58%',
                        },
                      },
                    ]}
                  >
                    {reactHTMLParser(answer)}
                  </Box>
                  {!!faqLinks?.length && (
                    <ul
                      css={theme => ({
                        listStyleType: 'none',
                        margin: theme.space.s0,
                        lineHeight: theme.lineHeights.lh1_75,
                      })}
                    >
                      {faqLinks.map(({ final_url, default_link_text }) => (
                        <li key={final_url}>
                          <Link
                            aria-describedby={`faq-${idx}-answer`}
                            href={final_url || ''}
                            iconAnimation="navigational"
                            underline="hover"
                            variant={linkVariant}
                          >
                            {default_link_text || withSmartling('mktg_ct_consideration_faq_link_text')}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Container>
    </Box>
  );
};

export default FAQSection;
